import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Portal {
  id: string
  name: string
  slug: string
  domain: string
  custom_domain: string | null
  status: string
  features_enabled: string[]
  visitors_today: number
  active_users: number
  theme: { primary_color: string }
}

interface Session {
  id: string
  user_email: string
  pages_viewed: number
  device: string
  browser: string
  last_activity: string
}

export default function CustomerPortalPage() {
  const [portals, setPortals] = useState<Portal[]>([])
  const [sessions, setSessions] = useState<Session[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedPortal, setSelectedPortal] = useState<Portal | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [portalsRes, sessionsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/customer-portal/portals'),
      fetch(API_BASE + '/customer-portal/sessions'),
      fetch(API_BASE + '/customer-portal/statistics')
    ])
    if (portalsRes.ok) {
      const p = (await portalsRes.json()).portals || []
      setPortals(p)
      if (p.length > 0) setSelectedPortal(p[0])
    }
    if (sessionsRes.ok) setSessions((await sessionsRes.json()).sessions || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'maintenance': return 'bg-yellow-100 text-yellow-800'
      case 'inactive': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getDeviceIcon = (device: string) => {
    switch (device) {
      case 'desktop': return '💻'
      case 'mobile': return '📱'
      case 'tablet': return '📟'
      default: return '🖥️'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Customer Portal Builder</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Portal</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Portals</div>
              <div className="text-2xl font-bold">{statistics.total_portals}</div>
              <div className="text-xs text-green-600">{statistics.active_portals} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Visitors Today</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.visitors_today?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Users</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_users?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Sessions</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.active_sessions}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {portals.map(portal => (
            <div key={portal.id}
                 className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${selectedPortal?.id === portal.id ? 'ring-2 ring-blue-500' : ''}`}
                 onClick={() => setSelectedPortal(portal)}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: portal.theme?.primary_color }}></div>
                <span className="font-semibold">{portal.name}</span>
              </div>
              <div className="text-sm text-gray-500 mb-2">{portal.domain}</div>
              <div className="flex justify-between items-center">
                <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(portal.status)}`}>{portal.status}</span>
                <span className="text-sm font-medium">{portal.visitors_today} visitors</span>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Active Sessions</h2></div>
            <div className="divide-y">
              {sessions.slice(0, 10).map(session => (
                <div key={session.id} className="p-4 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{getDeviceIcon(session.device)}</span>
                    <div>
                      <div className="font-medium">{session.user_email}</div>
                      <div className="text-sm text-gray-500">{session.browser} on {session.device}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">{session.pages_viewed} pages</div>
                    <div className="text-xs text-gray-500">{new Date(session.last_activity).toLocaleTimeString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedPortal && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: selectedPortal.theme?.primary_color }}></div>
                <h3 className="text-lg font-semibold">{selectedPortal.name}</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Domain</div>
                  <div className="font-medium">{selectedPortal.domain}</div>
                  {selectedPortal.custom_domain && (
                    <div className="text-sm text-blue-600">{selectedPortal.custom_domain}</div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded">
                    <div className="text-2xl font-bold text-blue-600">{selectedPortal.visitors_today}</div>
                    <div className="text-xs text-gray-500">visitors today</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded">
                    <div className="text-2xl font-bold text-green-600">{selectedPortal.active_users}</div>
                    <div className="text-xs text-gray-500">active users</div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-500 mb-2">Features Enabled</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedPortal.features_enabled.map((feature, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{feature}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
